import {
  collection,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Match } from '../../types';
import {
  BracketMatchDisplay,
  MundialMatchType,
  MundialTeam,
  MUNDIAL_LEAGUE,
} from '../../types/mundial';
import {
  BRACKET_ADVANCEMENT,
  KNOCKOUT_ROUNDS,
  PENDING_TEAM_LABEL,
  ROUND_OF_32_PAIRINGS,
  PAIRING_BY_SLOT,
} from '../../constants/mundialBracket';
import {
  allGroupStagesComplete,
  computeQualifiedTeams,
  resolveSlotToTeam,
} from './mundialQualificationService';
import { buildKnockoutPlaceholder } from './mundialMatchService';

export interface PopulateR32Options {
  /** @deprecated No usar — los orígenes vienen del Excel en Firestore */
  forcePairings?: boolean;
}

function getWinnerCode(match: Match): string | null {
  if (match.score_a === null || match.score_b === null) return match.winner_code ?? null;
  if (match.score_a > match.score_b) return match.team_a_code ?? null;
  if (match.score_b > match.score_a) return match.team_b_code ?? null;
  return match.winner_code ?? null;
}

/** Empate en eliminatoria: marcador tras 120 min; ganador por penales en winner_code. */
export function isKnockoutDrawNeedingPenaltyWinner(
  match: Match,
  scoreA: number,
  scoreB: number
): boolean {
  return (
    match.league === 'mundial_fifa' &&
    match.match_type !== 'group' &&
    match.match_type !== 'third_place' &&
    !!match.bracket_slot &&
    scoreA === scoreB
  );
}

function resolveAdvancerFromCode(
  match: Match,
  winnerCode: string
): { code: string; name: string; logo: string | null | undefined } | null {
  const code = winnerCode.toUpperCase();
  if (match.team_a_code?.toUpperCase() === code) {
    return { code: match.team_a_code!, name: match.team_a, logo: match.team_a_logo };
  }
  if (match.team_b_code?.toUpperCase() === code) {
    return { code: match.team_b_code!, name: match.team_b ?? '', logo: match.team_b_logo };
  }
  return null;
}

function hasCapturedResult(match: Match): boolean {
  return match.score_a !== null && match.score_b !== null;
}

function teamDisplay(
  name: string | null,
  code: string | null | undefined,
  flagUrl: string,
  teamsByCode: Map<string, MundialTeam>
): { name: string; code: string; flagUrl: string } {
  const label = name || PENDING_TEAM_LABEL;
  if (!code || label === PENDING_TEAM_LABEL) {
    return { name: PENDING_TEAM_LABEL, code: '', flagUrl: '' };
  }
  const team = teamsByCode.get(code.toUpperCase());
  return {
    name: team?.name ?? label,
    code: code.toUpperCase(),
    flagUrl: team?.flag_url ?? flagUrl,
  };
}

export function buildBracketDisplay(
  matches: Match[],
  teams: MundialTeam[],
  matchType: MundialMatchType
): BracketMatchDisplay[] {
  const teamsByCode = new Map(teams.map((t) => [t.code.toUpperCase(), t]));
  const seenSlots = new Set<string>();
  return matches
    .filter((m) => m.match_type === matchType && m.bracket_slot)
    .filter((m) => {
      const slot = m.bracket_slot!;
      if (seenSlots.has(slot)) return false;
      seenSlots.add(slot);
      return true;
    })
    .sort((a, b) => (a.bracket_slot || '').localeCompare(b.bracket_slot || ''))
    .map((m) => {
      const winner = getWinnerCode(m);
      const isPending =
        m.team_a === PENDING_TEAM_LABEL ||
        m.team_b === PENDING_TEAM_LABEL ||
        m.score_a === null ||
        m.score_b === null;

      const pairing = PAIRING_BY_SLOT.get(m.bracket_slot || '');
      const sourceA = (m as Match & { source_slot_a?: string }).source_slot_a;
      const sourceB = (m as Match & { source_slot_b?: string }).source_slot_b;

      return {
        id: m.id,
        bracketSlot: m.bracket_slot || '',
        matchType,
        teamA: teamDisplay(m.team_a, m.team_a_code, m.team_a_logo || '', teamsByCode),
        teamB: teamDisplay(m.team_b, m.team_b_code, m.team_b_logo || '', teamsByCode),
        sourceLabelA: pairing?.labelA ?? sourceA ?? undefined,
        sourceLabelB: pairing?.labelB ?? sourceB ?? undefined,
        scoreA: m.score_a,
        scoreB: m.score_b,
        winnerCode: winner,
        date: m.date ? new Date(m.date) : null,
        stadium: m.stadium ?? undefined,
        isPending,
      };
    });
}

export const mundialBracketService = {
  async seedKnockoutPlaceholders(startDate: Date): Promise<number> {
    const existing = await getDocs(
      query(collection(db, 'matches'), where('league', '==', MUNDIAL_LEAGUE))
    );
    const hasKnockout = existing.docs.some((d) => d.data().match_type === 'round_of_32');
    if (hasKnockout) return 0;

    const batch = writeBatch(db);
    let count = 0;
    let dayOffset = 30;

    ROUND_OF_32_PAIRINGS.forEach((pairing, i) => {
      const date = new Date(startDate);
      date.setDate(date.getDate() + dayOffset + i);
      const ref = doc(collection(db, 'matches'));
      const data = buildKnockoutPlaceholder(
        pairing.slot,
        'round_of_32',
        '16vos de final',
        date
      );
      batch.set(ref, {
        ...data,
        source_slot_a: pairing.sourceA,
        source_slot_b: pairing.sourceB,
      });
      count += 1;
    });

    const laterRounds: { type: MundialMatchType; label: string; slots: string[]; extraDays: number }[] = [
      {
        type: 'round_of_16',
        label: '8vos de final',
        slots: Array.from({ length: 8 }, (_, i) => `R16-${String(i + 1).padStart(2, '0')}`),
        extraDays: 50,
      },
      {
        type: 'quarter_final',
        label: 'Cuartos de final',
        slots: ['QF-01', 'QF-02', 'QF-03', 'QF-04'],
        extraDays: 58,
      },
      {
        type: 'semi_final',
        label: 'Semifinal',
        slots: ['SF-01', 'SF-02'],
        extraDays: 65,
      },
      {
        type: 'final',
        label: 'Final',
        slots: ['FINAL'],
        extraDays: 72,
      },
    ];

    laterRounds.forEach((round) => {
      round.slots.forEach((slot, i) => {
        const date = new Date(startDate);
        date.setDate(date.getDate() + round.extraDays + i);
        const ref = doc(collection(db, 'matches'));
        batch.set(ref, buildKnockoutPlaceholder(slot, round.type, round.label, date));
        count += 1;
      });
    });

    await batch.commit();
    return count;
  },

  /**
   * Rellena equipos en 16vos leyendo source_slot_a/b del documento (Excel).
   * No toca pronósticos. No sobrescribe source_slot ni marcadores oficiales.
   */
  async populateRoundOf32FromGroups(
    _options?: PopulateR32Options
  ): Promise<{ updated: number; skipped: boolean }> {
    const [teamsSnap, matchesSnap] = await Promise.all([
      getDocs(query(collection(db, 'mundial_teams'), where('league', '==', MUNDIAL_LEAGUE))),
      getDocs(query(collection(db, 'matches'), where('league', '==', MUNDIAL_LEAGUE))),
    ]);

    const teams = teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as MundialTeam[];
    const matches = matchesSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as Match[];

    if (!allGroupStagesComplete(teams, matches)) {
      return { updated: 0, skipped: true };
    }

    const { qualified, thirdPlaceRanking } = computeQualifiedTeams(teams, matches);
    const teamsByCode = new Map(teams.map((t) => [t.code.toUpperCase(), t]));

    let updated = 0;

    for (const m of matches) {
      if (m.match_type !== 'round_of_32' || !m.bracket_slot) continue;
      if (hasCapturedResult(m)) continue;

      const sourceA =
        m.match_type === 'round_of_32' && m.bracket_slot
          ? PAIRING_BY_SLOT.get(m.bracket_slot)?.sourceA ?? (m as Match & { source_slot_a?: string }).source_slot_a
          : (m as Match & { source_slot_a?: string }).source_slot_a;
      const sourceB =
        m.match_type === 'round_of_32' && m.bracket_slot
          ? PAIRING_BY_SLOT.get(m.bracket_slot)?.sourceB ?? (m as Match & { source_slot_b?: string }).source_slot_b
          : (m as Match & { source_slot_b?: string }).source_slot_b;
      if (!sourceA || !sourceB) continue;

      const winnerSlot = [sourceA, sourceB].find((s) => /^1[A-L]$/.test(s));
      const contextFor = (source: string) =>
        /^3[A-L]+$/.test(source) && winnerSlot
          ? { opponentWinnerSlot: winnerSlot }
          : undefined;

      const teamA = resolveSlotToTeam(
        sourceA,
        qualified,
        thirdPlaceRanking,
        contextFor(sourceA)
      );
      const teamB = resolveSlotToTeam(
        sourceB,
        qualified,
        thirdPlaceRanking,
        contextFor(sourceB)
      );
      if (!teamA || !teamB) continue;

      const infoA = teamsByCode.get(teamA.teamCode);
      const infoB = teamsByCode.get(teamB.teamCode);

      if (
        m.team_a_code === teamA.teamCode &&
        m.team_b_code === teamB.teamCode
      ) {
        continue;
      }

      await updateDoc(doc(db, 'matches', m.id), {
        team_a: teamA.teamName,
        team_b: teamB.teamName,
        team_a_code: teamA.teamCode,
        team_b_code: teamB.teamCode,
        team_a_logo: infoA?.flag_url ?? teamA.flagUrl,
        team_b_logo: infoB?.flag_url ?? teamB.flagUrl,
        updated_at: new Date(),
      });
      updated += 1;
    }

    return { updated, skipped: false };
  },

  async advanceWinnerFromMatch(match: Match): Promise<void> {
    if (!match.bracket_slot || match.match_type === 'group') return;
    if (match.score_a === null || match.score_b === null) return;

    let winnerCode: string | null = null;
    let winnerName: string | null = null;
    let winnerLogo: string | null | undefined = null;

    if (match.score_a > match.score_b) {
      winnerCode = match.team_a_code ?? null;
      winnerName = match.team_a;
      winnerLogo = match.team_a_logo;
    } else if (match.score_b > match.score_a) {
      winnerCode = match.team_b_code ?? null;
      winnerName = match.team_b;
      winnerLogo = match.team_b_logo;
    } else {
      if (!match.winner_code) return;
      const advancer = resolveAdvancerFromCode(match, match.winner_code);
      if (!advancer) return;
      winnerCode = advancer.code;
      winnerName = advancer.name;
      winnerLogo = advancer.logo;
    }

    if (!winnerCode || !winnerName) return;

    const advance = BRACKET_ADVANCEMENT[match.bracket_slot];
    if (!advance) return;

    const matchesSnap = await getDocs(
      query(
        collection(db, 'matches'),
        where('league', '==', MUNDIAL_LEAGUE),
        where('bracket_slot', '==', advance.nextSlot)
      )
    );

    if (matchesSnap.empty) return;

    const nextDoc = matchesSnap.docs[0];
    if (advance.position === 'team_a') {
      await updateDoc(doc(db, 'matches', nextDoc.id), {
        team_a: winnerName,
        team_a_code: winnerCode,
        team_a_logo: winnerLogo,
        updated_at: new Date(),
      });
    } else {
      await updateDoc(doc(db, 'matches', nextDoc.id), {
        team_b: winnerName,
        team_b_code: winnerCode,
        team_b_logo: winnerLogo,
        updated_at: new Date(),
      });
    }
    await updateDoc(doc(db, 'matches', match.id), {
      winner_code: winnerCode,
      updated_at: new Date(),
    });
  },

  getRoundsConfig() {
    return KNOCKOUT_ROUNDS;
  },
};
